import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { colors, fonts } from '../brand';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import Disclaimer from '../components/Disclaimer';

export default function QA() {
  const { user, token } = useAuth();
  const [qa, setQA] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openQuestion, setOpenQuestion] = useState(null);
  const [openMember, setOpenMember] = useState({});
  const [submitQ, setSubmitQ] = useState('');
  const [submitSent, setSubmitSent] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const isSubscriber = user?.subscription_status === 'active';

  useEffect(() => {
    axios.get(`${API}/api/quiz/qa`)
      .then(res => setQA(res.data.qa || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      await axios.post(`${API}/api/quiz/submit-question`, { question: submitQ }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmitSent(true);
      setSubmitQ('');
    } catch {
      setSubmitSent(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  const toggleMember = (ci) => {
    setOpenMember(prev => ({ ...prev, [ci]: !prev[ci] }));
  };

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Divorce Q&A — Real Questions, Plain-Language Answers | Elephants Answering</title>
        <meta name="description" content="Real divorce questions answered in plain language. No $300/hour required. Community Q&A from women who've navigated custody, hidden assets, court, and more." />
      </Helmet>
      <SiteNav />

      <main style={styles.main}>
        <div style={styles.inner}>
          <p style={styles.eyebrow}>COMMUNITY Q&A</p>
          <h1 style={styles.title}>Questions Women Are Actually Asking</h1>
          <p style={styles.subtitle}>
            Real questions, plain language. No $300/hour required.
          </p>

          {loading ? (
            <p style={styles.loadingText}>Loading...</p>
          ) : (
            <>
              {qa.map((category, ci) => {
                const freeItems = category.items.filter(item => item.free);
                const lockedItems = category.items.filter(item => !item.free);
                const memberOpen = openMember[ci];

                return (
                  <section key={ci} style={styles.section}>
                    <h2 style={styles.sectionHeading}>{category.category}</h2>
                    <div style={styles.list}>

                      {/* Free Q&As — full accordion */}
                      {freeItems.map((item, qi) => {
                        const key = `${ci}-free-${qi}`;
                        const isOpen = openQuestion === key;
                        return (
                          <div key={key} style={styles.qaItem}>
                            <button style={styles.qaQuestion} onClick={() => setOpenQuestion(isOpen ? null : key)}>
                              <span style={styles.qaQuestionText}>{item.q}</span>
                              <span style={styles.toggle}>{isOpen ? '−' : '+'}</span>
                            </button>
                            {isOpen && <p style={styles.answer}>{item.a}</p>}
                          </div>
                        );
                      })}

                      {/* Members section */}
                      {lockedItems.length > 0 && (
                        <div style={styles.memberBlock}>
                          <div style={styles.memberRow}>
                            {isSubscriber ? (
                              <button style={styles.memberToggle} onClick={() => toggleMember(ci)}>
                                <span style={styles.memberToggleText}>
                                  {memberOpen ? '−' : '+'} More for community members
                                </span>
                              </button>
                            ) : (
                              <Link to="/register" style={{ ...styles.memberToggle, textDecoration: 'none' }}>
                                <span style={styles.memberToggleText}>+ More for community members</span>
                              </Link>
                            )}
                            {!isSubscriber && (
                              <Link to="/register" style={styles.joinNowBtn}>
                                Join Now
                              </Link>
                            )}
                          </div>

                          {isSubscriber && memberOpen && (
                            <div style={styles.lockedList}>
                              {lockedItems.map((item, qi) => {
                                const key = `${ci}-locked-${qi}`;
                                const isOpen = openQuestion === key;
                                return (
                                  <div key={key} style={styles.lockedItem}>
                                    <button style={styles.qaQuestion} onClick={() => setOpenQuestion(isOpen ? null : key)}>
                                      <span style={styles.qaQuestionText}>{item.q}</span>
                                      <span style={styles.toggle}>{isOpen ? '−' : '+'}</span>
                                    </button>
                                    {isOpen && <p style={styles.answer}>{item.a}</p>}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  </section>
                );
              })}

              {isSubscriber && (
                <section style={styles.section}>
                  <h2 style={styles.sectionHeading}>What Did We Miss?</h2>
                  <p style={styles.memberNote}>Submit a question — if you have it, someone else does too. We'll add an answer within 48 hours.</p>
                  {submitSent ? (
                    <p style={styles.submitThanks}>Got it. We'll add an answer within 48 hours. — Amy</p>
                  ) : (
                    <form onSubmit={handleSubmit} style={styles.submitForm}>
                      <textarea
                        style={styles.submitInput}
                        placeholder="What's your question?"
                        value={submitQ}
                        onChange={e => setSubmitQ(e.target.value)}
                        rows={3}
                        required
                      />
                      <button style={styles.submitBtn} type="submit" disabled={submitLoading}>
                        {submitLoading ? 'Sending...' : 'Submit Question'}
                      </button>
                    </form>
                  )}
                </section>
              )}

              {!isSubscriber && (
                <div style={styles.cta}>
                  <p style={styles.ctaText}>Unlock every answer when you join.</p>
                  <Link to="/register" style={styles.ctaBtn}>
                    Join the Community
                  </Link>
                  <p style={styles.ctaTagline}>Free for Early Members</p>
                  <p style={styles.paywallNote}>
                    Already a member?{' '}
                    <Link to="/login" style={styles.paywallLink}>Sign in</Link>
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Disclaimer />
      <SiteFooter />
    </div>
  );
}

const styles = {
  page: { fontFamily: fonts.body, backgroundColor: colors.cream, minHeight: '100vh' },

  main: { padding: '64px 20px 80px' },
  inner: { maxWidth: '800px', margin: '0 auto' },
  loadingText: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px' },

  eyebrow: { fontFamily: fonts.body, color: colors.rose, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '12px' },
  title: { fontFamily: fonts.heading, color: colors.navy, fontSize: '36px', fontWeight: '600', marginBottom: '16px', letterSpacing: '0.3px' },
  subtitle: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '15px', lineHeight: '1.9', marginBottom: '56px' },

  section: { marginBottom: '52px' },
  sectionHeading: { fontFamily: fonts.heading, color: colors.navy, fontSize: '18px', fontWeight: '600', letterSpacing: '0.3px', marginBottom: '0', paddingBottom: '12px', borderBottom: `2px solid ${colors.rose}` },
  memberNote: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', fontStyle: 'italic', marginBottom: '20px', marginTop: '0' },

  list: { display: 'flex', flexDirection: 'column' },

  qaItem: { borderBottom: `1px solid ${colors.peach}` },
  qaQuestion: { width: '100%', background: 'none', border: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 4px', cursor: 'pointer', textAlign: 'left', gap: '16px' },
  qaQuestionText: { fontFamily: fonts.body, color: colors.navy, fontSize: '14px', lineHeight: '1.6', flex: 1 },
  toggle: { color: colors.rose, fontWeight: '700', fontSize: '18px', flexShrink: 0 },
  answer: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '1.8', padding: '0 4px 16px', margin: 0 },

  memberBlock: { borderBottom: `1px solid ${colors.peach}` },
  memberRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 4px', gap: '16px' },
  memberToggle: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' },
  memberToggleText: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', fontStyle: 'italic' },
  joinNowBtn: { backgroundColor: 'transparent', border: `1px solid ${colors.rose}`, color: colors.rose, padding: '6px 16px', fontSize: '12px', fontFamily: fonts.body, fontWeight: '600', letterSpacing: '0.5px', flexShrink: 0, textDecoration: 'none', display: 'inline-block' },

  lockedList: { paddingBottom: '8px' },
  lockedItem: { borderTop: `1px solid ${colors.peach}` },
  lockedQuestion: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '1.6', padding: '14px 4px', margin: 0, fontStyle: 'italic' },

  cta: { marginTop: '16px', padding: '48px 40px', backgroundColor: colors.navy, textAlign: 'center' },
  ctaText: { fontFamily: fonts.heading, color: colors.white, fontSize: '18px', marginBottom: '20px', fontWeight: '500' },
  ctaBtn: { backgroundColor: colors.rose, color: colors.white, textDecoration: 'none', padding: '14px 36px', fontSize: '14px', fontWeight: '600', fontFamily: fonts.body, letterSpacing: '0.5px', display: 'inline-block' },
  ctaTagline: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '12px' },
  paywallNote: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginTop: '16px' },
  paywallLink: { color: colors.white, textDecoration: 'underline' },

  submitForm: { display: 'flex', flexDirection: 'column', gap: '12px' },
  submitInput: { padding: '12px 16px', border: `1px solid ${colors.peach}`, fontFamily: fonts.body, fontSize: '14px', color: colors.navy, backgroundColor: colors.white, outline: 'none', resize: 'vertical', boxSizing: 'border-box', width: '100%' },
  submitBtn: { alignSelf: 'flex-start', backgroundColor: colors.navy, color: colors.white, border: 'none', padding: '12px 28px', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px' },
  submitThanks: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', fontStyle: 'italic', lineHeight: '1.8' },
};
